import { Skeleton } from '@mantine/core';
import { memo, useEffect, type RefObject } from 'react';

import ConnectedContestCard from '~/domains/contest/domains/common/components/ContestCard/ConnectedContestCard';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { Scroller } from '~/domains/design-system/components/Scroller/Scroller';
import testIds from '~/domains/home/testing.const';
import { useIsBiggerThanDesktop } from '~/domains/common/hooks/mediaQueries';

import { NEXT_BATCH_SIZE, RAIL_CARD_WIDTH, RAIL_SKELETON_HEIGHT } from '../../constants/rail';

import { useStyles } from './ContestRail.styles';

type RailSlideProps = {
  contest: Contest;
  promoIndex: number;
  promoSource: string;
  isContestThumbnailShown: boolean;
  isCountdownPillEnabled: boolean;
  sentinelRef: ((node: HTMLElement | null) => void) | undefined;
};

const RailSlide = memo(
  ({
    contest,
    promoIndex,
    promoSource,
    isContestThumbnailShown,
    isCountdownPillEnabled,
    sentinelRef,
  }: RailSlideProps) => {
    const { classes } = useStyles();

    return (
      <div ref={sentinelRef} className={classes.slide}>
        <ConnectedContestCard
          contest={contest}
          isContestThumbnailShown={isContestThumbnailShown}
          isNotAllowedInLocation={contest.is_allowed_in_location === false}
          isBadgeIconSectionEnabled={false}
          isBannerEnabled
          isCountdownPillEnabled={isCountdownPillEnabled}
          promoSource={promoSource}
          promoIndex={promoIndex}
        />
      </div>
    );
  }
);
RailSlide.displayName = 'RailSlide';

type ContestRailProps = {
  id: string;
  isTabPanel: boolean;
  tabValue: string;
  contests: Contest[];
  skeletonCount: number;
  isInitialLoading: boolean;
  scrollerRef: RefObject<HTMLDivElement>;
  trackRef: (node: HTMLElement | null) => void;
  sentinelRef: (node: HTMLElement | null) => void;
  promoSource: string;
  isContestThumbnailShown: boolean;
  isCountdownPillEnabled: boolean;
};

export function ContestRail({
  id,
  isTabPanel,
  tabValue,
  contests,
  skeletonCount,
  isInitialLoading,
  scrollerRef,
  trackRef,
  sentinelRef,
  promoSource,
  isContestThumbnailShown,
  isCountdownPillEnabled,
}: ContestRailProps) {
  const { classes, theme } = useStyles();
  const isDesktop = useIsBiggerThanDesktop();

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [tabValue, scrollerRef]);

  const lastIndex = contests.length - 1;
  const visibleSkeletonCount = isInitialLoading ? NEXT_BATCH_SIZE : skeletonCount;

  return (
    <div
      className={classes.railContainer}
      id={id}
      role={isTabPanel ? 'tabpanel' : undefined}
      data-test-id={testIds.components.condensedCarousels.rail}
    >
      <Scroller
        ref={scrollerRef}
        scrollAmount={(scrollerRef.current?.clientWidth ?? 0) / 3 || RAIL_CARD_WIDTH}
        backgroundColor={theme.colors.gray2[0]}
        controlVariant='floating'
        withFade={isDesktop}
        withControls={isDesktop}
      >
        <div ref={trackRef} className={classes.rail}>
          {!isInitialLoading &&
            contests.map((contest, index) => (
              <RailSlide
                key={contest.id}
                contest={contest}
                promoIndex={index + 1}
                promoSource={promoSource}
                isContestThumbnailShown={isContestThumbnailShown}
                isCountdownPillEnabled={isCountdownPillEnabled}
                sentinelRef={index === lastIndex ? sentinelRef : undefined}
              />
            ))}
          {Array.from({ length: visibleSkeletonCount }, (_, index) => (
            <div
              key={index}
              className={classes.slide}
              data-test-id={testIds.components.condensedCarousels.skeleton}
            >
              <Skeleton height={RAIL_SKELETON_HEIGHT} radius="lg" />
            </div>
          ))}
        </div>
      </Scroller>
    </div>
  );
}
